/**
 * Artikelliste mit Bestandsanzeige.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { formatPrice, formatQuantity, getUnit, variantAttributesText } from '@erp/shared';

import { api, withQuery } from '../api/client';
import type { Item, ListResponse } from '../api/types';
import { useAuth } from '../auth';
import { PageHeader } from '../components/Layout';
import { Badge, EmptyState, LoadingBlock, TextInput } from '../components/ui';
import type { Category } from './CategoriesPage';

/** Artikelliste; mit `kind` nur eine Art, z. B. die Set-Artikel. */
export function ItemsPage({ kind }: { kind?: 'set' | 'parent' } = {}) {
  const navigate = useNavigate();
  const { can } = useAuth();

  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [belowMinStock, setBelowMinStock] = useState(false);
  const [categoryId, setCategoryId] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<{ categories: Category[] }>('/api/categories'),
  });

  const categoryById = new Map(
    (categories?.categories ?? []).map((category) => [category.id, category]),
  );

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['items', kind ?? 'all', search, showInactive, belowMinStock, categoryId, offset],
    queryFn: () =>
      api.get<ListResponse<Item>>(
        withQuery('/api/items', {
          q: search || undefined,
          active: showInactive ? 'all' : 'true',
          belowMinStock: belowMinStock ? 'true' : 'false',
          categoryId: categoryId || undefined,
          kind,
          limit,
          offset,
        }),
      ),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHeader
        title={kind === 'set' ? 'Set-Artikel' : kind === 'parent' ? 'Elternartikel' : 'Artikel'}
        subtitle={data ? `${data.total} ${data.total === 1 ? 'Artikel' : 'Artikel'}` : undefined}
        actions={
          can('masterdata:write') ? (
            <Link className="btn btn-primary" to={kind ? `/artikel/neu?kind=${kind}` : '/artikel/neu'}>
              Neu anlegen
            </Link>
          ) : null
        }
      />

      <div className="card">
        <div className="table-toolbar">
          <TextInput
            value={search}
            onChange={(value) => {
              setSearch(value);
              setOffset(0);
            }}
            placeholder="Suche nach Nummer, Bezeichnung, EAN …"
          />
          <select
            className="select input"
            style={{ width: 220 }}
            value={categoryId}
            onChange={(event) => {
              setCategoryId(event.target.value);
              setOffset(0);
            }}
          >
            <option value="">Alle Warengruppen</option>
            {(categories?.categories ?? [])
              .filter((category) => category.active)
              .map((category) => (
                <option key={category.id} value={category.id}>
                  {category.number ? `${category.number} · ${category.name}` : category.name}
                </option>
              ))}
          </select>
          <label className="checkbox-row" style={{ fontSize: 13 }}>
            <input
              type="checkbox"
              checked={belowMinStock}
              onChange={(event) => {
                setBelowMinStock(event.target.checked);
                setOffset(0);
              }}
            />
            Nur unter Meldebestand
          </label>
          <label className="checkbox-row" style={{ fontSize: 13 }}>
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(event) => {
                setShowInactive(event.target.checked);
                setOffset(0);
              }}
            />
            Inaktive anzeigen
          </label>
        </div>

        {isLoading ? <LoadingBlock /> : null}
        {isError ? <div className="table-empty">{(error as Error).message}</div> : null}

        {data && !isLoading ? (
          data.items.length === 0 ? (
            <EmptyState
              title={search || belowMinStock ? 'Keine Treffer' : kind === 'set' ? 'Noch keine Set-Artikel' : 'Noch keine Artikel erfasst'}
              hint={kind === 'set' && !search ? 'Ein Set ist ein Artikel mit Art „Set-Artikel" und einer Stückliste.' : undefined}
            />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th className="shrink">Nummer</th>
                    <th>Bezeichnung</th>
                    <th className="shrink">Warengruppe</th>
                    <th className="numeric">VK netto</th>
                    <th className="numeric">EK netto</th>
                    <th className="numeric">Bestand</th>
                    <th className="shrink">Einheit</th>
                    <th className="shrink"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item) => {
                    const belowStock =
                      item.isStockItem && item.minStock > 0 && item.stockQuantity < item.minStock;

                    return (
                      <tr
                        key={item.id}
                        className="clickable"
                        onClick={() => navigate(`/artikel/${item.id}`)}
                      >
                        <td className="mono">{item.number}</td>
                        <td>
                          <div style={{ fontWeight: 500 }}>
                            {item.name}
                            {item.kind === 'parent' ? (
                              <>
                                {' '}
                                <Badge kind="accent">Elternartikel</Badge>
                              </>
                            ) : null}
                            {item.kind === 'set' ? (
                              <>
                                {' '}
                                <Badge kind="accent">Set</Badge>
                              </>
                            ) : null}
                            {item.parentItemId ? (
                              <>
                                {' '}
                                <Badge>Variante</Badge>
                              </>
                            ) : null}
                          </div>
                          {variantAttributesText(item.variantAttributes) ? (
                            <div className="faint">{variantAttributesText(item.variantAttributes)}</div>
                          ) : null}
                          {item.manufacturerNumber ? (
                            <div className="faint">Hersteller-Nr. {item.manufacturerNumber}</div>
                          ) : null}
                        </td>
                        <td className="muted">
                          {item.categoryId ? (categoryById.get(item.categoryId)?.name ?? '') : ''}
                        </td>
                        <td className="numeric">{formatPrice(item.salesPrice)}</td>
                        <td className="numeric muted">{formatPrice(item.purchasePrice)}</td>
                        <td className="numeric">
                          {item.isStockItem ? (
                            <span style={belowStock ? { color: 'var(--warning)', fontWeight: 600 } : undefined}>
                              {formatQuantity(item.stockQuantity)}
                            </span>
                          ) : (
                            <span className="faint">—</span>
                          )}
                        </td>
                        <td className="faint">{safeUnit(item.unit)}</td>
                        <td>
                          {!item.active ? <Badge kind="warning">Inaktiv</Badge> : null}
                          {belowStock ? <Badge kind="warning">Nachbestellen</Badge> : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        ) : null}

        {data && data.total > limit ? (
          <div className="table-footer">
            <span>
              {offset + 1} – {Math.min(offset + limit, data.total)} von {data.total}
            </span>
            <div className="row">
              <button
                type="button"
                className="btn btn-sm"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - limit))}
              >
                Zurück
              </button>
              <button
                type="button"
                className="btn btn-sm"
                disabled={offset + limit >= data.total}
                onClick={() => setOffset(offset + limit)}
              >
                Weiter
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}

/** Unbekannte Einheiten sollen die Liste nicht zum Absturz bringen. */
function safeUnit(code: string): string {
  try {
    return getUnit(code).short;
  } catch {
    return code;
  }
}
